import type { TypingAttempt } from '../../lib/types';

interface TypingStatsProps {
  attempts: TypingAttempt[];
}

export default function TypingStats({ attempts }: TypingStatsProps) {
  if (attempts.length === 0) {
    return (
      <div className="card" style={{ textAlign: 'center', color: 'var(--color-text-secondary)' }}>
        <p>هنوز تمرینی انجام نشده</p>
        <p style={{ fontSize: '0.85rem', marginTop: 4 }}>اولین تمرین را شروع کنید!</p>
      </div>
    );
  }

  const recent = attempts.slice(-10).reverse();
  const bestWpm = Math.max(...attempts.map((a) => a.wpm));
  const bestAccuracy = Math.max(...attempts.map((a) => a.accuracy));
  const maxWpmForChart = Math.max(bestWpm, 1);

  // For the bar chart we use the last 10 in chronological order
  const chartData = attempts.slice(-10);

  return (
    <div>
      {/* Recent attempts table */}
      <div className="card" style={{ overflowX: 'auto' }}>
        <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>آخرین تمرین‌ها</h3>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.9rem',
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: '2px solid var(--color-border)',
                textAlign: 'center',
              }}
            >
              <th style={{ padding: '8px 4px' }}>تاریخ</th>
              <th style={{ padding: '8px 4px' }}>سرعت (WPM)</th>
              <th style={{ padding: '8px 4px' }}>دقت</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((attempt, i) => {
              const date = new Date(attempt.timestamp);
              const dateStr = date.toLocaleDateString('fa-IR', {
                month: 'short',
                day: 'numeric',
              });
              const timeStr = date.toLocaleTimeString('fa-IR', {
                hour: '2-digit',
                minute: '2-digit',
              });
              const isBestWpm = attempt.wpm === bestWpm;
              const isBestAcc = attempt.accuracy === bestAccuracy;

              return (
                <tr
                  key={i}
                  style={{
                    borderBottom: '1px solid var(--color-border)',
                    textAlign: 'center',
                  }}
                >
                  <td style={{ padding: '10px 4px', fontSize: '0.8rem' }}>
                    {dateStr}
                    <br />
                    <span style={{ color: 'var(--color-text-secondary)' }}>{timeStr}</span>
                  </td>
                  <td
                    style={{
                      padding: '10px 4px',
                      fontWeight: isBestWpm ? 700 : 400,
                      color: isBestWpm ? 'var(--color-primary)' : 'inherit',
                    }}
                  >
                    {Math.round(attempt.wpm)}
                    {isBestWpm && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginRight: 4,
                          fontSize: '0.7rem',
                          background: 'var(--color-primary)',
                          color: '#fff',
                          padding: '1px 6px',
                          borderRadius: 4,
                          verticalAlign: 'middle',
                        }}
                      >
                        بهترین
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: '10px 4px',
                      fontWeight: isBestAcc ? 700 : 400,
                      color: isBestAcc ? 'var(--color-success)' : 'inherit',
                    }}
                  >
                    {Math.round(attempt.accuracy)}%
                    {isBestAcc && (
                      <span
                        style={{
                          display: 'inline-block',
                          marginRight: 4,
                          fontSize: '0.7rem',
                          background: 'var(--color-success)',
                          color: '#fff',
                          padding: '1px 6px',
                          borderRadius: 4,
                          verticalAlign: 'middle',
                        }}
                      >
                        بهترین
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* WPM bar chart */}
      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginBottom: 12, fontSize: '1rem' }}>نمودار سرعت تایپ</h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 6,
            height: 120,
            direction: 'ltr',
          }}
        >
          {chartData.map((attempt, i) => {
            const heightPct = (attempt.wpm / maxWpmForChart) * 100;
            const isMax = attempt.wpm === bestWpm;
            return (
              <div
                key={i}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                  justifyContent: 'flex-end',
                }}
              >
                <span
                  style={{
                    fontSize: '0.65rem',
                    color: 'var(--color-text-secondary)',
                    marginBottom: 2,
                    fontFamily: 'var(--font-en)',
                  }}
                >
                  {Math.round(attempt.wpm)}
                </span>
                <div
                  style={{
                    width: '100%',
                    maxWidth: 32,
                    height: `${Math.max(heightPct, 4)}%`,
                    background: isMax
                      ? 'var(--color-primary)'
                      : 'var(--color-primary-light)',
                    borderRadius: '4px 4px 0 0',
                    transition: 'height 0.3s ease',
                    opacity: isMax ? 1 : 0.7,
                  }}
                />
              </div>
            );
          })}
        </div>
        <div
          style={{
            textAlign: 'center',
            fontSize: '0.75rem',
            color: 'var(--color-text-secondary)',
            marginTop: 6,
            fontFamily: 'var(--font-en)',
            direction: 'ltr',
          }}
        >
          WPM over last {chartData.length} attempts
        </div>
      </div>
    </div>
  );
}
